/**
 * Skeleton — placeholder shapes that approximate the final layout
 * while data loads. Replaces the bare "Loading…" text the portal
 * used everywhere.
 *
 * Idiom borrowed from DemoExperience.tsx but extracted so every page
 * uses the same shapes + animation. The `.artomeui-skeleton` class
 * (defined in index.css) carries the soft-pulse animation.
 *
 * Usage:
 *   <Skeleton.Card />               // a card-sized block
 *   <Skeleton.Tile />               // a metric tile
 *   <Skeleton.TableRow cols={7} />  // one <tr> of skeleton <td>s
 *   <Skeleton.Line w="40%" />       // a single horizontal line
 */
const SkeletonRoot = ({
  className = '',
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) => (
  <div
    className={`artomeui-skeleton ${className}`}
    aria-hidden="true"
    style={style}
  />
);

function SkeletonLine({
  w = '100%',
  h = 12,
  className = '',
}: {
  w?: string | number;
  h?: number;
  className?: string;
}) {
  return (
    <SkeletonRoot
      className={className}
      style={{ width: typeof w === 'number' ? `${w}px` : w, height: `${h}px` }}
    />
  );
}

function SkeletonCard({ height = 96 }: { height?: number }) {
  return (
    <div className="card space-y-2">
      <SkeletonLine w="40%" h={10} />
      <SkeletonLine w="70%" h={24} />
      <SkeletonLine w="55%" h={10} />
      <div style={{ height: Math.max(0, height - 76) }} />
    </div>
  );
}

function SkeletonTile() {
  return (
    <div className="card space-y-2">
      <SkeletonLine w="50%" h={10} />
      <SkeletonLine w="70%" h={22} />
    </div>
  );
}

/**
 * One placeholder row for a table. Renders `cols` <td>s each
 * containing a single skeleton bar. Widths vary across columns so
 * the placeholder looks like real data, not a uniform mosaic.
 */
function SkeletonTableRow({ cols }: { cols: number }) {
  // Pseudo-random per-column widths anchored to col index so a row
  // looks varied but stays the same across re-renders.
  const widths = ['80%', '55%', '45%', '60%', '70%', '40%', '50%', '65%', '55%', '70%', '45%'];
  return (
    <tr className="border-b border-border last:border-0">
      {Array.from({ length: cols }, (_, i) => (
        <td key={i} className="py-2.5 pr-4">
          <SkeletonLine w={widths[i % widths.length]!} h={10} />
        </td>
      ))}
    </tr>
  );
}

/**
 * `<Skeleton.TableRows>` — convenience wrapper that renders N rows.
 * Used inside an existing <tbody> so the page reserves the right
 * vertical space immediately.
 */
function SkeletonTableRows({ rows, cols }: { rows: number; cols: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, i) => (
        <SkeletonTableRow key={i} cols={cols} />
      ))}
    </>
  );
}

export const Skeleton = {
  Line: SkeletonLine,
  Card: SkeletonCard,
  Tile: SkeletonTile,
  TableRow: SkeletonTableRow,
  TableRows: SkeletonTableRows,
};
